import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { useUI } from "../providers/MoleUIContext";

export type ItemAliasEntry = {
	alias: string;
	last_used: string | null;
	is_last_used: boolean;
};

export type ItemAliasResponse = {
	catalog_name: string;
	last_used: string | null;
	aliases: ItemAliasEntry[];
};

const emptyResponse = (): ItemAliasResponse => ({
	catalog_name: "",
	last_used: null,
	aliases: [],
});

export const useItemAliases = (itemId: number | null | undefined) => {
	const { api } = useAuth();
	const { queueReRun } = useUI();
	const [data, setData] = useState<ItemAliasResponse>(emptyResponse);
	const [loading, setLoading] = useState(false);
	const requestIdRef = useRef(0);

	const fetchAliases = useCallback(async (id: number) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);

		const res = await api.get(`items/${id}/aliases`);

		if (requestId !== requestIdRef.current) {
			return null;
		}

		if (res?.status === 200) {
			setData(res.data as ItemAliasResponse);
			setLoading(false);
			return res.data as ItemAliasResponse;
		}

		if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => fetchAliases(id));
		} else {
			console.error("Failed to fetch item aliases:", res);
			setData(emptyResponse());
		}

		setLoading(false);
		return null;
	}, [api, queueReRun]);

	useEffect(() => {
		if (!itemId || itemId < 1) {
			requestIdRef.current += 1;
			setData(emptyResponse());
			setLoading(false);
			return;
		}

		fetchAliases(itemId);
	}, [itemId, fetchAliases]);

	return {
		data,
		loading,
		refresh: () => (itemId && itemId > 0 ? fetchAliases(itemId) : Promise.resolve(null)),
	};
};